import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { displaySkuSearchQuery, normalizeSkuSearchQuery } from '@theme/vanagain-sku-search';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback();

    this.#redirectSkuPrefixURL();
    this.refs.searchPageInput.value = displaySkuSearchQuery(this.refs.searchPageInput.value);
    this.refs.searchPageInput.form?.addEventListener('submit', this.#normalizeSubmittedQuery);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.searchPageInput.form?.removeEventListener('submit', this.#normalizeSubmittedQuery);
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };

  #normalizeSubmittedQuery = () => {
    const searchInput = this.refs.searchPageInput;
    const normalizedQuery = normalizeSkuSearchQuery(searchInput.value);
    if (normalizedQuery !== searchInput.value.trim()) {
      searchInput.value = normalizedQuery;
    }
  };

  #redirectSkuPrefixURL() {
    const url = new URL(window.location.href);
    const query = url.searchParams.get('q') ?? '';
    const normalizedQuery = normalizeSkuSearchQuery(query);

    if (!query || normalizedQuery === query) return;

    url.searchParams.set('q', normalizedQuery);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('options[fields]', 'title,product_type,variants.title,vendor,variants.sku,variants.barcode,tag');
    window.location.replace(url.toString());
  }
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
